
import { Button } from '../../../../components/ui/button';
import type { TableExplorerInboundForeignKey } from '../../../../types/tableExplorer';
import type { LooseRelationship } from '../../../../types/electron';
import { InboundForeignKeyExplorer } from './InboundForeignKeyExplorer';
import type { DatabaseEngineId } from '../../../../core/engines';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type RowDetailsRelatedDataTabProps = {
  readonly connectionId: string;
  readonly database: string;
  readonly schema: string;
  readonly tableName: string;
  readonly parentRow: Record<string, unknown>;
  readonly supportsInboundForeignKeyExplorer: boolean;
  readonly inboundForeignKeys: TableExplorerInboundForeignKey[];
  readonly isInboundForeignKeysLoading: boolean;
  readonly inboundForeignKeysError: unknown;
  readonly onRetryInboundForeignKeys: () => void;
  readonly engineId?: DatabaseEngineId;
  /** Slice 3 — when supplied, the inbound-FK "Open" button drills via the
   *  nav tree instead of opening a new TE tab. */
  readonly onDrillInboundFk?: (args: {
    foreignKey: TableExplorerInboundForeignKey;
    looseRel: LooseRelationship | undefined;
  }) => void;
};

export function RowDetailsRelatedDataTab({
  connectionId,
  database,
  schema,
  tableName,
  parentRow,
  supportsInboundForeignKeyExplorer,
  inboundForeignKeys,
  isInboundForeignKeysLoading,
  inboundForeignKeysError,
  onRetryInboundForeignKeys,
  engineId,
  onDrillInboundFk,
}: RowDetailsRelatedDataTabProps) {
  if (!supportsInboundForeignKeyExplorer) {
    return (
      <div className="rounded-md border border-border bg-background/60 p-3 text-xs text-muted-foreground">
        <div className="font-medium text-foreground mb-1">Related Data</div>
        <div>
          Related Data lists rows from other tables that reference this table via foreign keys. This database engine
          does not currently support inbound foreign key discovery.
        </div>
      </div>
    );
  }

  if (isInboundForeignKeysLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-2 text-xs text-muted-foreground">
        <RandomDotmSpinner size={14} />
        Discovering foreign keys referencing {schema ? `${schema}.` : ''}
        {tableName}…
      </div>
    );
  }

  if (inboundForeignKeysError) {
    return (
      <div className="rounded-md border border-border bg-background/60 p-3">
        <div className="text-xs text-destructive">Failed to load related data</div>
        <div className="text-xs text-muted-foreground mt-1">
          {inboundForeignKeysError instanceof Error ? inboundForeignKeysError.message : String(inboundForeignKeysError)}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs mt-2"
          onClick={onRetryInboundForeignKeys}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (inboundForeignKeys.length === 0) {
    return (
      <div className="rounded-md border border-border bg-background/60 p-3 text-xs text-muted-foreground">
        <div className="font-medium text-foreground mb-1">No related data</div>
        <div>
          No foreign keys were found that reference {schema ? `${schema}.` : ''}
          {tableName}. When present, each foreign key can be expanded to preview matching rows or opened in Table
          Explorer with an auto-applied filter.
        </div>
      </div>
    );
  }

  return (
    <InboundForeignKeyExplorer
      connectionId={connectionId}
      database={database}
      parentRow={parentRow}
      foreignKeys={inboundForeignKeys}
      engineId={engineId}
      onDrillInboundFk={onDrillInboundFk}
    />
  );
}

